/**
 * Agent A4 + A5 — Remediation Advisor & Drift Triage (V85-4-2).
 *
 * A4 reads structured deny objects (from gate evaluation) and drift events,
 * suggests remediation as unified diffs (never pushed), and outputs
 * requirement IDs only (not raw policy text).
 *
 * A5 is bundled here since both read the same denial/drift events.
 * It proposes materiality reclassification when appropriate.
 *
 * Both run through the V85-4-0 agent harness. Output writes to
 * `findings` queue — proposals only, deterministic code decides.
 *
 * Auth model: N/A (pure agent function, workspace-scoped via harness).
 */
import { defineTemplate, field, referenceDerived } from '../egress/data-classes.js';
import { LlmRefusedError } from '../egress/gateway.js';
import { explainRemediationForRequirement, triageDrift, } from './agent-remediation-service.js';
// ---- Agent definitions ------------------------------------------------------
export const A4_A5_DEFINITION = {
    agent_id: 'A4+A5',
    prompt_version: '2026.09.20',
    queue_target: 'findings',
    schema_version: '1.0',
};
export const A4_A5_OUTPUT_SCHEMA = {
    type: 'object',
    required: ['system_id', 'summary', 'remediation_suggestions', 'drift_triage'],
    properties: {
        system_id: { type: 'string' },
        summary: { type: 'string' },
        remediation_suggestions: { type: 'array' },
        drift_triage: { type: 'array' },
    },
};
// ---- Prompt template --------------------------------------------------------
const REMEDIATION_PROMPT_TEMPLATE = `You are an AI governance remediation advisor. Given a set of gate denials and drift events for an AI system, suggest remediation actions.

## System
Name: {system_name}
System ID: {system_id}

## Gate denials ({denial_count} total)
{denials_section}

## Drift events ({drift_count} total)
{drift_section}

## Instructions

For each denial:
1. Explain what evidence or action would satisfy the requirement
2. Reference the requirement ID and obligation ref only — do NOT include raw policy text
3. If applicable, draft a patch as a unified diff (comments only, never pushed)
4. List accepted evidence types from the control spec

For each drift event:
1. Classify materiality: "material" if it could change risk posture, "minor" if operational
2. Propose reclassification when appropriate (e.g. "requires_review")

IMPORTANT:
- Output requirement IDs only, never raw policy text
- Patch drafts are suggestions — they are never pushed
- Each suggestion must cite a specific requirement_id from the input

## Output format (JSON)

Return:
- summary: string
- remediation_suggestions: array of { requirement_id, obligation_ref, explanation, accepted_evidence, patch_draft?, severity }
- drift_triage: array of { alert_id, control_id, drift_kind, materiality, rationale, proposed_reclassification? }
`;
/** ADR-0073: the A4/A5 prompt as a reviewed template (gate denials and drift events are tenant data). */
export const A4_TEMPLATE = defineTemplate('agent.a4.remediation_drift', '2026.09.20', REMEDIATION_PROMPT_TEMPLATE, ['system_name', 'system_id', 'denial_count', 'denials_section', 'drift_count', 'drift_section']);
// ---- Deterministic logic ----------------------------------------------------
/**
 * Build remediation suggestions deterministically from denials + requirements.
 * The LLM may enhance explanations, but these are always present.
 */
export function deterministicRemediation(input) {
    const requirementMap = new Map(input.requirements.map(r => [r.id, r]));
    const controlSpecMap = new Map(input.control_specs.map(cs => [cs.control_id, cs]));
    const suggestions = [];
    for (const denial of input.denials) {
        const req = requirementMap.get(denial.requirement_id);
        if (!req)
            continue;
        const spec = controlSpecMap.get(denial.control_id);
        if (!spec) {
            // No spec — still produce a minimal suggestion
            suggestions.push({
                requirement_id: denial.requirement_id,
                obligation_ref: denial.obligation_ref,
                explanation: `Gate denied for requirement ${denial.requirement_id}: ${denial.deny_reason}. ` +
                    `Address the denial reason for obligation ${denial.obligation_ref}.`,
                accepted_evidence: [],
                severity: req.severity,
            });
            continue;
        }
        const explanation = explainRemediationForRequirement(req, spec);
        suggestions.push({
            requirement_id: explanation.requirement_id,
            obligation_ref: explanation.obligation_ref,
            explanation: explanation.explanation,
            accepted_evidence: explanation.accepted_evidence,
            patch_draft: explanation.patch_draft,
            severity: req.severity,
        });
    }
    return suggestions;
}
/**
 * Deterministic drift triage using pure logic from agent-remediation-service.
 */
export function deterministicDriftTriage(input) {
    return input.drift_events.map(event => {
        const result = triageDrift(event);
        return {
            alert_id: event.alert_id,
            control_id: event.control_id,
            drift_kind: event.kind,
            materiality: result.materiality,
            rationale: result.rationale,
            proposed_reclassification: result.proposed_reclassification,
        };
    });
}
// ---- Agent function ---------------------------------------------------------
export const remediationDriftAgent = async (llm, input) => {
    const data = input.data;
    if (!data.system_id) {
        throw new Error('A4+A5 remediation/drift agent: system_id is required');
    }
    if (!Array.isArray(data.denials)) {
        throw new Error('A4+A5 remediation/drift agent: denials array is required');
    }
    if (!Array.isArray(data.drift_events)) {
        throw new Error('A4+A5 remediation/drift agent: drift_events array is required');
    }
    const validRequirementIds = new Set(data.requirements.map(r => r.id));
    // Step 1: Deterministic logic (always runs)
    const deterministicSuggestions = deterministicRemediation(data);
    const deterministicTriage = deterministicDriftTriage(data);
    // Step 2: LLM-enhanced analysis
    let llmSuggestions = [];
    let llmTriage = [];
    let summary = '';
    try {
        // ADR-0073: through the egress gateway; a refusal keeps the deterministic
        // analysis (parity agent_a4_remediation_drift).
        const result = await llm.run({
            task: 'agent.a4.remediation_drift', template: A4_TEMPLATE,
            schemaHint: A4_A5_OUTPUT_SCHEMA, options: { temperature: 0.1, maxTokens: 4096 },
            slots: {
                system_name: field('ai_system.description', data.system_name),
                system_id: field('ai_system.description', data.system_id),
                denial_count: referenceDerived('count', data.denials.length),
                denials_section: field('release.gate_events', data.denials.length > 0
                    ? data.denials.map(d => `- [${d.requirement_id}] ${d.obligation_ref}: ${d.deny_reason}`).join('\n')
                    : 'No denials.'),
                drift_count: referenceDerived('count', data.drift_events.length),
                drift_section: field('release.gate_events', data.drift_events.length > 0
                    ? data.drift_events.map(e => `- [${e.alert_id}] ${e.kind} on ${e.control_id}: ${e.detail}`).join('\n')
                    : 'No drift events.'),
            },
        });
        if (result.status !== 'ok')
            throw new LlmRefusedError(result);
        summary = result.output.summary ?? '';
        // Validate LLM suggestions — strip any referencing unknown requirements
        llmSuggestions = (result.output.remediation_suggestions ?? []).filter(s => validRequirementIds.has(s.requirement_id));
        llmTriage = result.output.drift_triage ?? [];
    }
    catch {
        // LLM failure: deterministic-only
        summary = `Deterministic-only remediation for ${data.system_name}: ` +
            `${deterministicSuggestions.length} suggestion(s), ${deterministicTriage.length} triage(s). LLM unavailable.`;
    }
    // Merge: deterministic first, then unique LLM additions
    const deterministicReqIds = new Set(deterministicSuggestions.map(s => s.requirement_id));
    const uniqueLlmSuggestions = llmSuggestions.filter(s => !deterministicReqIds.has(s.requirement_id));
    const deterministicAlertIds = new Set(deterministicTriage.map(t => t.alert_id));
    const uniqueLlmTriage = llmTriage.filter(t => !deterministicAlertIds.has(t.alert_id));
    const allSuggestions = [...deterministicSuggestions, ...uniqueLlmSuggestions];
    const allTriage = [...deterministicTriage, ...uniqueLlmTriage];
    if (!summary) {
        summary = `Remediation analysis for ${data.system_name}: ` +
            `${allSuggestions.length} remediation suggestion(s), ${allTriage.length} drift triage(s).`;
    }
    // Build provenance
    const provenance = {
        system_id: {
            source: 'input.system_id',
            span: data.system_id,
            confidence: 1.0,
        },
        summary: {
            source: 'agent.analysis',
            span: summary.slice(0, 200),
            confidence: 0.9,
        },
        remediation_suggestions: {
            source: 'agent.analysis',
            span: `${allSuggestions.length} suggestions (${deterministicSuggestions.length} deterministic)`,
            confidence: 0.9,
        },
        drift_triage: {
            source: 'agent.analysis',
            span: `${allTriage.length} triage entries (${deterministicTriage.length} deterministic)`,
            confidence: 0.9,
        },
    };
    // Per-suggestion provenance
    for (let i = 0; i < allSuggestions.length; i++) {
        const s = allSuggestions[i];
        provenance[`suggestion.${i}`] = {
            source: `requirement:${s.requirement_id}`,
            span: s.obligation_ref,
            confidence: 1.0,
        };
    }
    // Per-triage provenance
    for (let i = 0; i < allTriage.length; i++) {
        const t = allTriage[i];
        provenance[`triage.${i}`] = {
            source: `drift:${t.alert_id}`,
            span: `${t.drift_kind} on ${t.control_id}`,
            confidence: t.materiality === 'material' ? 1.0 : 0.85,
        };
    }
    return {
        target: 'findings',
        proposal: {
            system_id: data.system_id,
            summary,
            remediation_suggestions: allSuggestions,
            drift_triage: allTriage,
        },
        provenance,
    };
};
//# sourceMappingURL=agent-remediation-drift-service.js.map